import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import "./cssDash/ReceptionnisteDashboard.css";

interface Mariage {
  _id: string;
  bride: string;
  groom: string;
  date: string;
  heure: string;
  lieu: string;
}

interface Evenement {
  _id: string;
  type: string;
  dateISO: string;
  heureISO: string;
  lieu: string;
  province?: string;
}

interface Anniversaire {
  _id: string;
  personne: string;
  age: number;
  dateISO: string;
  heureISO: string;
  lieu: string;
  province?: string;
}

interface User {
  name: string;
  role: string;
}

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  user?: User;
  mariages?: Mariage[];
  evenements?: Evenement[];
  anniversaires?: Anniversaire[];
}

type EventType = "mariages" | "evenements" | "anniversaires";

const ReceptionnisteDashboard: React.FC = () => {
  const [eventType, setEventType] = useState<EventType>("mariages");
  const [mariages, setMariages] = useState<Mariage[]>([]);
  const [evenements, setEvenements] = useState<Evenement[]>([]);
  const [anniversaires, setAnniversaires] = useState<Anniversaire[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "Date non définie";
    
    try {
      if (dateString.includes('-')) {
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR');
        }
      }
      
      const moisMap: { [key: string]: number } = {
        'janvier': 0, 'février': 1, 'mars': 2, 'avril': 3,
        'mai': 4, 'juin': 5, 'juillet': 6, 'août': 7,
        'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11
      };
      
      const mots = dateString.toLowerCase().split(' ');
      const jour = parseInt(mots[0]);
      const moisNom = mots[1];
      const annee = parseInt(mots[2]);
      
      if (moisMap[moisNom] !== undefined && !isNaN(jour) && !isNaN(annee)) {
        const date = new Date(annee, moisMap[moisNom], jour);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('fr-FR');
        }
      }
      
      return "Date invalide";
    } catch {
      return "Date invalide";
    }
  };

  useEffect(() => {
    fetchUserInfo();
  }, []);

  useEffect(() => {
    if (eventType === "mariages") {
      fetchMariages();
    } else if (eventType === "evenements") {
      fetchEvenements();
    } else {
      fetchAnniversaires();
    }
  }, [eventType]);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await api.get<ApiResponse>("/auth/me");
      
      if (response.data.success) {
        setUser(response.data.user || null);
      }
    } catch (err: any) {
      console.error("Erreur chargement user:", err);
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
      }
    }
  };

  const fetchMariages = async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setError("Vous n'êtes pas connecté");
        setLoading(false);
        return;
      }

      const response = await api.get<ApiResponse>("/receptionniste/mariages");

      if (response.data.success) {
        setMariages(response.data.mariages || []);
      } else {
        setError(response.data.message || "Erreur lors du chargement");
      }
    } catch (err: any) {
      console.error("Erreur chargement mariages:", err);
      
      if (err.response?.status === 403) {
        setError("Token invalide ou expiré. Veuillez vous reconnecter.");
      } else if (err.response?.status === 401) {
        setError("Non authentifié. Veuillez vous reconnecter.");
      } else {
        setError("Erreur de connexion au serveur");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEvenements = async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setError("Vous n'êtes pas connecté");
        setLoading(false);
        return;
      }

      const response = await api.get<ApiResponse>("/receptionniste/evenements");

      if (response.data.success) {
        setEvenements(response.data.evenements || []);
      } else {
        setError(response.data.message || "Erreur lors du chargement");
      }
    } catch (err: any) {
      console.error("Erreur chargement événements:", err);
      
      if (err.response?.status === 403) {
        setError("Token invalide ou expiré. Veuillez vous reconnecter.");
      } else if (err.response?.status === 401) {
        setError("Non authentifié. Veuillez vous reconnecter.");
      } else {
        setError("Erreur de connexion au serveur");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnniversaires = async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setError("Vous n'êtes pas connecté");
        setLoading(false);
        return;
      }

      const response = await api.get<ApiResponse>("/receptionniste/anniversaires");

      if (response.data.success) {
        setAnniversaires(response.data.anniversaires || []);
      } else {
        setError(response.data.message || "Erreur lors du chargement");
      }
    } catch (err: any) {
      console.error("Erreur chargement anniversaires:", err);
      
      if (err.response?.status === 403) {
        setError("Token invalide ou expiré. Veuillez vous reconnecter.");
      } else if (err.response?.status === 401) {
        setError("Non authentifié. Veuillez vous reconnecter.");
      } else {
        setError("Erreur de connexion au serveur");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("mariageActif");
    navigate("/");
  };

  const handleSelectMariage = (mariageId: string) => {
    localStorage.setItem("mariageActif", mariageId);
    navigate(`/scan/${mariageId}`);
  };

  const handleSelectEvenement = (evenementId: string) => {
    localStorage.setItem("evenementActif", evenementId);
    navigate(`/scan-evenement/${evenementId}`);
  };

  const handleSelectAnniversaire = (anniversaireId: string) => {
    localStorage.setItem("anniversaireActif", anniversaireId);
    navigate(`/scan-anniversaire/${anniversaireId}`);
  };

  const isToday = (dateString: string) => {
    const today = new Date().toDateString();
    const formattedDate = formatDate(dateString);
    const eventDate = new Date(dateString).toDateString();
    return today === eventDate;
  };

  const getEvenementTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      bapteme: "Baptême",
      fete: "Fête",
      conference: "Conférence",
      autre: "Événement"
    };
    return labels[type] || type;
  };

  const getCurrentList = () => {
    if (eventType === "mariages") return mariages;
    if (eventType === "evenements") return evenements;
    return anniversaires;
  };

  const renderMariageCard = (item: Mariage) => (
    <div 
      key={item._id} 
      className={`event-card ${isToday(item.date) ? 'today' : ''}`}
      onClick={() => handleSelectMariage(item._id)}
    >
      <div className="event-card-header">
        <h3>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
          </svg>
          {item.bride} & {item.groom}
        </h3>
        {isToday(item.date) && (
          <span className="today-badge">Aujourd'hui</span>
        )}
      </div>
      
      <div className="event-card-body">
        <p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7v-5z" fill="currentColor"/>
          </svg>
          <strong>Date:</strong> {formatDate(item.date)}
        </p>
        <p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
          </svg>
          <strong>Heure:</strong> {item.heure}
        </p>
        <p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
          </svg>
          <strong>Lieu:</strong> {item.lieu}
        </p>
      </div>
      
      <button className="select-btn">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
        </svg>
        Scanner les invitations
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" fill="currentColor"/>
        </svg>
      </button>
    </div>
  );

  const renderEvenementCard = (item: Evenement) => (
    <div 
      key={item._id} 
      className={`event-card ${isToday(item.dateISO) ? 'today' : ''}`}
      onClick={() => handleSelectEvenement(item._id)}
    >
      <div className="event-card-header">
        <h3>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
          </svg>
          {getEvenementTypeLabel(item.type)}
        </h3>
        {isToday(item.dateISO) && (
          <span className="today-badge">Aujourd'hui</span>
        )}
      </div>
      
      <div className="event-card-body">
        <p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
          </svg>
          <strong>Date:</strong> {formatDate(item.dateISO)}
        </p>
        <p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
          </svg>
          <strong>Heure:</strong> {item.heureISO}
        </p>
        <p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
          </svg>
          <strong>Lieu:</strong> {item.lieu}
        </p>
      </div>
      
      <button className="select-btn">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
        </svg>
        Scanner les invitations
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" fill="currentColor"/>
        </svg>
      </button>
    </div>
  );

  const renderAnniversaireCard = (item: Anniversaire) => (
    <div 
      key={item._id} 
      className={`event-card ${isToday(item.dateISO) ? 'today' : ''}`}
      onClick={() => handleSelectAnniversaire(item._id)}
    >
      <div className="event-card-header">
        <h3>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 6c1.11 0 2-.9 2-2 0-.38-.1-.73-.29-1.03L12 0l-1.71 2.97c-.19.3-.29.65-.29 1.03 0 1.1.9 2 2 2zm4.6 4.47l-1.07-1.86L13 11.6V17h2v-4.4l1.6-2.13zM17 22v-2h-4v-4h-2v4H7v2h10zM8 12.4L5.6 9.6 4 11.6V17h2v-4.4l2-2.2z" fill="currentColor"/>
          </svg>
          {item.personne} ({item.age} ans)
        </h3>
        {isToday(item.dateISO) && (
          <span className="today-badge">Aujourd'hui</span>
        )}
      </div>
      
      <div className="event-card-body">
        <p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
          </svg>
          <strong>Date:</strong> {formatDate(item.dateISO)}
        </p>
        <p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
          </svg>
          <strong>Heure:</strong> {item.heureISO}
        </p>
        <p>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
          </svg>
          <strong>Lieu:</strong> {item.lieu}
        </p>
      </div>
      
      <button className="select-btn">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
        </svg>
        Scanner les invitations
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" fill="currentColor"/>
        </svg>
      </button>
    </div>
  );

  const renderCard = (item: any) => {
    if (eventType === "mariages") return renderMariageCard(item);
    if (eventType === "evenements") return renderEvenementCard(item);
    return renderAnniversaireCard(item);
  };

  const currentList = getCurrentList();

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={() => navigate("/")} className="btn-login">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" fill="currentColor"/>
          </svg>
          Retour à la connexion
        </button>
      </div>
    );
  }

  return (
    <div className="receptionniste-container">
      <div className="header">
        <div className="header-top">
          <h1>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
            </svg>
            Bonjour, <span className="user-name">{user?.name || 'Réceptionniste'}</span>
          </h1>
          <button onClick={handleLogout} className="logout-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor"/>
            </svg>
            Déconnexion
          </button>
        </div>
        <p className="subtitle">Sélectionnez l'événement du jour</p>
      </div>

      <div className="event-tabs">
        <button 
          className={`tab-btn ${eventType === "mariages" ? "active" : ""}`}
          onClick={() => setEventType("mariages")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 9V7c0-1.1-.9-2-2-2h-4c0-1.66-1.34-3-3-3S8 3.34 8 5H4c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h4c0 1.66 1.34 3 3 3s3-1.34 3-3h4c1.1 0 2-.9 2-2z" fill="currentColor"/>
          </svg>
          Mariages ({mariages.length})
        </button>
        <button 
          className={`tab-btn ${eventType === "evenements" ? "active" : ""}`}
          onClick={() => setEventType("evenements")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="currentColor"/>
          </svg>
          Événements ({evenements.length})
        </button>
        <button 
          className={`tab-btn ${eventType === "anniversaires" ? "active" : ""}`}
          onClick={() => setEventType("anniversaires")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 6c1.11 0 2-.9 2-2 0-.38-.1-.73-.29-1.03L12 0l-1.71 2.97c-.19.3-.29.65-.29 1.03 0 1.1.9 2 2 2zm4.6 4.47l-1.07-1.86L13 11.6V17h2v-4.4l1.6-2.13zM17 22v-2h-4v-4h-2v4H7v2h10zM8 12.4L5.6 9.6 4 11.6V17h2v-4.4l2-2.2z" fill="currentColor"/>
          </svg>
          Anniversaires ({anniversaires.length})
        </button>
      </div>

      <div className="events-list">
        {currentList.length === 0 ? (
          <p className="empty-message">Aucun {eventType === "mariages" ? "mariage" : eventType === "evenements" ? "événement" : "anniversaire"} assigné</p>
        ) : (
          currentList.map(item => renderCard(item))
        )}
      </div>
    </div>
  );
};

export default ReceptionnisteDashboard;